import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LayoutGroup } from "framer-motion";
import "@/App.css";
import { LanguageProvider } from "@/context/LanguageContext";
import { CartProvider } from "@/context/CartContext";
import { useLeadCaptureTrigger } from "@/hooks/useLeadCaptureTrigger";
import { useExitIntent } from "@/hooks/useExitIntent";
import { useScrollVelocity } from "@/hooks/useScrollVelocity";
import { useIdleSettle } from "@/hooks/useIdleSettle";
import { bootstrapSmartPreload } from "@/lib/smartPreload";
import Navbar from "@/components/sections/Navbar";
import Hero from "@/components/sections/Hero";
import TrustBar from "@/components/sections/TrustBar";
import FlagshipProduct from "@/components/sections/FlagshipProduct";
import ValueIncludes from "@/components/sections/ValueIncludes";
import ProductStoryCarousel from "@/components/ProductStoryCarousel";
import Collection from "@/components/sections/Collection";
import SizeGuide from "@/components/sections/SizeGuide";
import WhyMoissanite from "@/components/sections/WhyMoissanite";
import MacroVideo from "@/components/sections/MacroVideo";
import DiamondViewer3D from "@/components/sections/DiamondViewer3D";
import QualityDetails from "@/components/sections/QualityDetails";
import Packaging from "@/components/sections/Packaging";
import HowOrder from "@/components/sections/HowOrder";
import Shipping from "@/components/sections/Shipping";
import FounderStory from "@/components/sections/FounderStory";
import Reviews from "@/components/sections/Reviews";
import FAQ from "@/components/sections/FAQ";
import FinalCta from "@/components/sections/FinalCta";
import Footer from "@/components/sections/Footer";
import CartDrawer from "@/components/sections/CartDrawer";
import FloatingWhatsApp from "@/components/sections/FloatingWhatsApp";
import StickyBuyBar from "@/components/sections/StickyBuyBar";
import LeadCapture from "@/components/sections/LeadCapture";
import ExitIntentModal from "@/components/ExitIntentModal";
import ScrollProgress from "@/components/ScrollProgress";
import CinematicScene from "@/components/CinematicScene";
import LuxeCursor from "@/components/LuxeCursor";
import DebugPanel from "@/pages/DebugPanel";
import ProductPage from "@/pages/ProductPage";

function AppShell() {
  // ── Scroll behaviour hardening ────────────────────────────────────────────
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if ("scrollRestoration" in window.history) {
        window.history.scrollRestoration = "manual";
      }
    } catch (_e) { /* noop */ }
    const hasHash = !!window.location.hash;
    const params = new URLSearchParams(window.location.search);
    const storyRaw = parseInt(params.get("story") || "", 10);
    const hasStoryDeepLink = Number.isFinite(storyRaw) && storyRaw >= 2;
    if (!hasHash && !hasStoryDeepLink) {
      window.scrollTo(0, 0);
    }
  }, []);

  // ── Mark body once the user has scrolled past the hero (mobile WhatsApp gate)
  useEffect(() => {
    if (typeof window === "undefined") return;
    let ticking = false;
    const update = () => {
      const hero = document.querySelector("[data-testid='hero-section']");
      if (!hero) return;
      const bottom = hero.getBoundingClientRect().bottom;
      const past = bottom < 120;
      document.body.setAttribute("data-scrolled-past-hero", past ? "1" : "0");
      ticking = false;
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const [leadOpen, setLeadOpen] = useState(false);
  const [leadSource, setLeadSource] = useState("homepage_modal");
  const [exitOpen, setExitOpen] = useState(false);
  const { shouldOpen, setShouldOpen } = useLeadCaptureTrigger({ enabled: true });
  const { shouldOpen: exitShouldOpen, setShouldOpen: setExitShouldOpen } = useExitIntent({ enabled: true });
  useScrollVelocity();
  useIdleSettle();

  // Smart preload: prefetch imágenes de la siguiente sección al aproximarse el cursor
  useEffect(() => bootstrapSmartPreload(), []);

  // When the auto-trigger fires, sync into local state so we can also open manually
  useEffect(() => {
    if (shouldOpen && !leadOpen) {
      setLeadSource("homepage_modal");
      setLeadOpen(true);
      setShouldOpen(false);
    }
  }, [shouldOpen, leadOpen, setShouldOpen]);

  // Exit-intent → open the parting-offer modal (once per device via hook).
  useEffect(() => {
    if (exitShouldOpen && !exitOpen && !leadOpen) {
      setExitOpen(true);
      setExitShouldOpen(false);
    }
  }, [exitShouldOpen, exitOpen, leadOpen, setExitShouldOpen]);

  // Dev helper: window.__keloOpenExit() force-opens the modal (for QA / previews).
  useEffect(() => {
    if (typeof window !== "undefined" && process.env.NODE_ENV !== "production") {
      window.__keloOpenExit = () => setExitOpen(true);
    }
  }, []);

  const openLead = (src = "footer") => {
    setLeadSource(src);
    setLeadOpen(true);
  };

  return (
    <div className="App">
      <Navbar />
      <main>
        <Hero />
        <TrustBar />
        <CinematicScene intensity="strong"><FlagshipProduct /></CinematicScene>
        <CinematicScene intensity="strong"><ProductStoryCarousel /></CinematicScene>
        <CinematicScene intensity="strong"><Collection /></CinematicScene>
        <CinematicScene><SizeGuide /></CinematicScene>
        <CinematicScene intensity="strong"><WhyMoissanite /></CinematicScene>
        <CinematicScene intensity="strong"><MacroVideo /></CinematicScene>
        <CinematicScene intensity="strong"><DiamondViewer3D /></CinematicScene>
        <CinematicScene><QualityDetails /></CinematicScene>
        <CinematicScene><Packaging /></CinematicScene>
        <CinematicScene><HowOrder /></CinematicScene>
        <CinematicScene><Shipping /></CinematicScene>
        <CinematicScene intensity="strong"><FounderStory /></CinematicScene>
        <CinematicScene><Reviews /></CinematicScene>
        <CinematicScene><FAQ /></CinematicScene>
        <CinematicScene intensity="strong"><FinalCta /></CinematicScene>
      </main>
      <Footer onOpenLead={openLead} />
      <CartDrawer onAbandon={(src) => openLead(src || "cart_abandon")} />
      <FloatingWhatsApp />
      <StickyBuyBar />
      <LeadCapture
        open={leadOpen}
        onOpenChange={setLeadOpen}
        source={leadSource}
      />
      <ExitIntentModal open={exitOpen} onClose={() => setExitOpen(false)} />
    </div>
  );
}

function App() {
  // Ruta debug protegida — `?debug=<token>` renderiza el DebugPanel
  // sin cargar el resto del site (menos JS, más rápido de inspeccionar).
  const params =
    typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
  const debugToken = params?.get("debug");
  if (debugToken) {
    return <DebugPanel token={debugToken} />;
  }

  return (
    <LanguageProvider>
      <CartProvider>
        <BrowserRouter>
          <LayoutGroup>
            <ScrollProgress />
            <LuxeCursor />
            <Routes>
              <Route path="/" element={<AppShell />} />
              <Route path="/products/:slug" element={<ProductPage />} />
            </Routes>
          </LayoutGroup>
        </BrowserRouter>
      </CartProvider>
    </LanguageProvider>
  );
}

export default App;
